/**
 * TenantBrandingProvider — runtime OKLCH CSS variable injection.
 *
 * Mount this near the app root (inside `BrandingProvider` +
 * `ThemeProvider`) to enable per-tenant white-labelling.
 *
 * ## Responsibility
 *
 * 1. Calls `useTenantBranding()` to get the current tenant's branding
 *    via React Query (re-fetches on tenant context change).
 *
 * 2. Reads the current colour mode from `ThemeProvider` via `useTheme()`.
 *
 * 3. On every `(branding, mode)` change, calls `applyBrandingToDocument()`
 *    which writes OKLCH values to `document.documentElement` inline
 *    styles. When `branding` is null / `useDefaultTheme === true`, the
 *    function instead *clears* all inline overrides so the platform's
 *    OKLCH tokens from `index.css @theme` / `.dark` take effect.
 *
 * ## Relationship to existing providers
 *
 * ```
 * BrandingProvider         ← fetches + stores branding in React state
 *   ThemeProvider          ← reads branding, applies HSL CSS vars (legacy)
 *     TenantBrandingProvider ← THIS: applies OKLCH CSS vars (overrides HSL)
 *       <Outlet />
 * ```
 *
 * `TenantBrandingProvider` writes OKLCH values *after* `ThemeProvider`
 * writes HSL values, so any Tailwind class that references
 * `var(--color-primary)` will pick up the OKLCH value (higher specificity
 * via `inline style` vs. the CSS-sheet `.dark` rule).
 *
 * ## Opt-in fallback
 *
 * If `tenant_branding` row is missing or has `useDefaultTheme: true`,
 * `applyBrandingToDocument` removes all inline overrides. The platform's
 * default OKLCH values from `index.css` are restored without a page reload.
 *
 * ## No additional network calls
 *
 * `useTenantBranding` wraps `useBrandingQuery` which is shared with the
 * branding editor. React Query deduplicates the request — only one
 * `GET /branding` call is made regardless of how many components call
 * the hook.
 */

import { useEffect, ReactNode } from 'react';
import { useTenantBranding } from '@/lib/use-tenant-branding';
import { applyBrandingToDocument } from '@/lib/apply-tenant-branding';
import { useTheme } from '@/components/theme-provider';

interface TenantBrandingProviderProps {
  children: ReactNode;
}

/**
 * Renders its children unchanged; the only side-effect is writing CSS
 * variables to `document.documentElement` when branding or colour mode
 * changes.
 */
export function TenantBrandingProvider({ children }: TenantBrandingProviderProps) {
  const { branding } = useTenantBranding();
  const { mode } = useTheme();

  useEffect(() => {
    applyBrandingToDocument(branding, mode);
  }, [branding, mode]);

  return <>{children}</>;
}
